import { useEffect, useState } from "react";
import Modal from "../../shared/components/Modal";
import InfoIconWithTooltip from "../../shared/components/InfoIconWithTooltip";
import { isEmail, isNumeric } from "validator";
import { toast } from "react-toastify";
import appApi from "../../api";
import isURL from "validator/lib/isURL";
import Input from "shared/components/Input";
import Checkbox from "shared/components/Checkbox";

const LINK_PREFIX = "https://";

/**
 * @typedef {{
 *  id?: string;
 *  link_name: string;
 *  appointment_email_recipient: string;
 *  coconut_software_service_id_override: string;
 *  custom_button_link: string;
 *  custom_button_text: string;
 * }} CollectorFormData
 */

/** @type {CollectorFormData} */
const DEFAULT_COLLECTOR_FORM = {
    id: undefined, // Only need the ID for editing
    link_name: "",
    appointment_email_recipient: "",
    coconut_software_service_id_override: "",
    advisor_direct_link: false,
    custom_button_text: null,
    custom_button_link: null,
};

/**
 *
 * @param {object} props
 * @param {string} props.title
 * @param {string} props.saveButtonText
 * @param {(collector: object) => void} props.onCollectorCreated Called when a link is created.
 * @param {(collector: object) => void} props.onCollectorUpdated Called when a link is updated.
 * @param {object} props.collectorToUpdate If this is set, the dialog will be used to update an existing collector.
 * @param {() => void} props.onClose
 * @param {boolean} props.isOpen Whether the dialog should be open or not.
 */
function CreateOrUpdateSurveyCollectorDialog({
    title,
    saveButtonText,
    onCollectorCreated,
    onCollectorUpdated,
    collectorToUpdate,
    onClose,
    isOpen,
}) {
    /** @type {[CollectorFormData, React.Dispatch<React.SetStateAction<CollectorFormData>>]} */
    const [collectorForm, setCollectorForm] = useState({ ...DEFAULT_COLLECTOR_FORM });

    useEffect(() => {
        setCollectorForm(collectorToUpdate ? { ...collectorToUpdate, custom_button_link: collectorToUpdate.custom_button_link?.replace(LINK_PREFIX, "") } : { ...DEFAULT_COLLECTOR_FORM });
    }, [collectorToUpdate]);

    function validateForm() {
        const errors = [];

        if (!collectorForm.link_name) {
            errors.push("Link name is required");
        }
        if (!isEmail(collectorForm.appointment_email_recipient)) {
            errors.push("Invalid email address");
        }

        // Coconut Software service ID isn't required, so it doesn't need to be validated if it's blank.
        if (
            collectorForm.coconut_software_service_id_override &&
            typeof collectorForm.coconut_software_service_id_override !== "number" &&
            !isNumeric(collectorForm.coconut_software_service_id_override)
        ) {
            errors.push("Coconut software service ID must be a number");
        }

        if (collectorForm.custom_button_link && !isURL(LINK_PREFIX + collectorForm.custom_button_link, {protocols: ["http", "https"]})) {
            errors.push("Custom button link is invalid");
        }

        return errors;
    }

    async function onSaveCollector() {
        let closeModal = true;

        const errors = validateForm();
        if (errors.length > 0) {
            toast.error(
                <>
                    {errors.length === 1 ? (
                        <>{errors[0]}</>
                    ) : (
                        <ul style={{ marginLeft: "24px" }}>
                            {errors.map((error, index) => (
                                <li key={`generate-collector-error-${index}`} style={{ listStyleType: "disc" }}>
                                    {error}
                                </li>
                            ))}
                        </ul>
                    )}
                </>,
            );
            return;
        }

        try {
            if (collectorToUpdate) {
                const updatedCollector = await updateCollector();
                onCollectorUpdated?.(updatedCollector);
            } else {
                const createdCollector = await createCollector();
                onCollectorCreated?.({
                    ...createdCollector,
                    response_count: 0, // The number of responses isn't stored in the table so set the response count here
                });
            }
        } catch (error) {
            console.error(error);

            closeModal = false; // Keep the modal open for better UX

            if (error.response?.status === 409) {
                toast.error("A survey with this name already exists");
            } else {
                toast.error("Failed to generate survey link");
            }
        } finally {
            if (closeModal) {
                close();
            }
        }
    }

    async function createCollector() {
        console.log(collectorForm.advisor_direct_link);
        const response = await appApi.post("/company/generate/link", {
            link_name: collectorForm.link_name,
            appointment_email_recipient: collectorForm.appointment_email_recipient,
            coconut_software_service_id: collectorForm.coconut_software_service_id_override || null,
            advisor_direct_link: collectorForm.advisor_direct_link,
            custom_button_text: collectorForm.custom_button_text,
            custom_button_link: LINK_PREFIX + collectorForm.custom_button_link,
        });

        return response.data;
    }

    async function updateCollector() {
        const _collectorToUpdate = {
            ...collectorForm,
            custom_button_link: LINK_PREFIX + collectorForm.custom_button_link,
            coconut_software_service_id_override: collectorForm.coconut_software_service_id_override || null,
        };

        // Backend supports updating an array of values by the UI only allows updating one link name at a time
        await appApi.patch("/company/survey-links", [_collectorToUpdate]);

        return _collectorToUpdate;
    }

    function close() {
        onClose?.();
        resetForm();
    }

    function resetForm() {
        setCollectorForm({ ...DEFAULT_COLLECTOR_FORM });
    }

    return (
        <Modal
            title={title}
            isOpen={isOpen}
            onCancel={() => {
                close();
            }}
            customButtonProps={[
                {
                    children: saveButtonText,
                    onClick: onSaveCollector,
                    disabled: !collectorForm.link_name || !collectorForm.appointment_email_recipient, // Coconut service ID isn't required
                },
            ]}
            style={{
                width: "calc(15% + 200px)",
                minWidth: "fit-content",
                maxWidth: "100%",
            }}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div>
                    <label
                        htmlFor="collector-name"
                        className="paragraph-5-semibold"
                        style={{ color: "var(--colors-primary-slate-700)" }}
                    >
                        Name
                    </label>
                    <Input
                        id="collector-name"
                        type="text"
                        value={collectorForm.link_name}
                        onChange={(value) => {
                            setCollectorForm({ ...collectorForm, link_name: value });
                        }}
                        style={{ width: "100%", color: "var(--colors-primary-slate-900)" }}
                    />
                </div>

                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <label
                            htmlFor="book-appointment-email-to"
                            className="paragraph-5-semibold"
                            style={{ color: "var(--colors-primary-slate-700)" }}
                        >
                            Appointment email recipient
                        </label>

                        <InfoIconWithTooltip
                            id="appointment-email-recipient-info-tooltip"
                            content={
                                <div style={{ width: "325px" }}>
                                    If Coconut Software hasn't been set up, requests to book an appointment to discuss a
                                    respondent's financial health will be sent to this email address.
                                    <br />
                                    <br />
                                    Additionally, this address will receive an email if the appointment can't be booked
                                    through Coconut Software for any reason (e.g., service outage, or incorrect setup).
                                </div>
                            }
                        />
                    </div>
                    <Input
                        id="book-appointment-email-to"
                        type="text"
                        value={collectorForm.appointment_email_recipient}
                        onChange={(value) => {
                            setCollectorForm({ ...collectorForm, appointment_email_recipient: value });
                        }}
                        style={{ width: "100%", color: "var(--colors-primary-slate-900)" }}
                    />
                </div>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <label
                            htmlFor="advisor-direct-link"
                            className="paragraph-5-semibold"
                            style={{ color: "var(--colors-primary-slate-700)" }}
                        >
                            Advisor direct link
                        </label>
                        <InfoIconWithTooltip
                            id="advisor-direct-link-info-tooltip"
                            content={
                                <div style={{ width: "325px" }}>
                                    If set, survey results will be emailed directly to the above email upon survey
                                    completion.
                                </div>
                            }
                        />
                        <Checkbox
                            id="advisor-direct-link"
                            small
                            disabled={collectorToUpdate}
                            value={collectorForm.advisor_direct_link}
                            onChange={(e) => {
                                setCollectorForm({
                                    ...collectorForm,
                                    advisor_direct_link: e,
                                });
                            }}
                            style={{ marginLeft: "1em" }}
                        />
                    </div>
                </div>
                {collectorForm.advisor_direct_link && (
                    <>
                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <label
                                    htmlFor="custom-button-text"
                                    className="paragraph-5-semibold"
                                    style={{ color: "var(--colors-primary-slate-700)" }}
                                >
                                    Custom button text
                                </label>

                                <InfoIconWithTooltip
                                    id="custom-button-text-tooltip"
                                    content={
                                        <div style={{ width: "325px" }}>
                                            Set to enable a button in the top-left of the results page, labeled with the
                                            provided text and link. Leave empty to display no button.
                                        </div>
                                    }
                                />
                            </div>
                            <Input
                                id="custom-button-text"
                                type="text"
                                value={collectorForm.custom_button_text}
                                disabled={!collectorForm.advisor_direct_link}
                                onChange={(value) => {
                                    setCollectorForm({ ...collectorForm, custom_button_text: value });
                                }}
                                style={{ width: "100%", color: "var(--colors-primary-slate-900)" }}
                            />
                        </div>
                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <label
                                    htmlFor="custom-button-link"
                                    className="paragraph-5-semibold"
                                    style={{ color: "var(--colors-primary-slate-700)" }}
                                >
                                    Custom button link
                                </label>
                            </div>
                            <Input
                                id="custom-button-link"
                                type="text"
                                prefix={LINK_PREFIX}
                                value={collectorForm.custom_button_link}
                                disabled={!collectorForm.advisor_direct_link}
                                onChange={(value) => {
                                    setCollectorForm({ ...collectorForm, custom_button_link: value });
                                }}
                                style={{ width: "100%", color: "var(--colors-primary-slate-900)" }}
                            />
                        </div>
                    </>
                )}

                <div>
                    <label
                        htmlFor="coconut-software-service-id"
                        className="paragraph-5-semibold"
                        style={{ color: "var(--colors-primary-slate-700)" }}
                    >
                        Coconut Software Service ID
                    </label>
                    <Input
                        id="coconut-software-service-id"
                        type="text"
                        value={collectorForm.coconut_software_service_id_override}
                        onChange={(value) => {
                            setCollectorForm({
                                ...collectorForm,
                                coconut_software_service_id_override: value,
                            });
                        }}
                        style={{ width: "100%", color: "var(--colors-primary-slate-900)" }}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default CreateOrUpdateSurveyCollectorDialog;
